import { debounce } from 'lodash';
/**
   * @ngdoc directive
   * @name adjust.directive:search
   * @scope
   * @restrict E
   *
   * @description
   * Search box used for various lists like apps and trackers
   *
   * @param {string} title title to show as placeholder
   * @param {string=standard} name namespace of the search box
   * @param {Function} onChange on search term change callback
   * @param {string} searchTerm current search term
   * @param {string=300} debounce debounce parameter
   * @param {boolean=} disabled disable searching e.g. in case of remote search
   *
   */
function search() {
  'ngInject';
  return {
    restrict: 'E',
    scope: {},
    template: require('./search.html'),
    controller: 'SearchController',
    controllerAs: 'vm',
    bindToController: {
      title: '@',
      name: '@',
      onChange: '&',
      onFocus: '&',
      query: '@',
      searchTerm: '=?',
      debounce: '@',
      disabled: '=?',
      inputTestid: '@?',
    }
  };
}

/**
   * @ngdoc controller
   * @name adjust.controller:SearchController
   *
   * @description
   * All the functionality of the {@link adjust.directive:search search} directive
   *
   */
function SearchController($timeout, $scope, $location, Helper) {
  'ngInject';
  var vm = this;

  vm.name = vm.name || 'standard';
  vm.debounce = parseInt(vm.debounce || 300, 10);
  vm.isFocused = false;

  vm.changeDelayed = debounce(change, vm.debounce);
  vm.change = change;
  vm.reset = reset;
  vm.onKeyDown = onKeyDown;
  vm.onFocusChange = onFocusChange;

  Helper.ready(_handleReady);
  $scope.$on('search:' + vm.query + ':reset', _handleReset);
  $scope.$on('$destroy', _handleDestroy);

  /**
   * @ngdoc method
   * @name _handleReady
   * @methodOf adjust.controller:SearchController
   *
   * @description
   * Get query string from url and init change if query parameter is defined at all
   *
   * @private
   */
  function _handleReady() {

    var params = $location.search();

    if (vm.query) {
      vm.searchTerm = params[vm.query] || '';
    }

  }

  /**
   * @param {Object} e
   */
  function onKeyDown(e) {

    // Clear the field with escape
    if (e.keyCode === 27 && vm.searchTerm !== '') {
      reset();
      return;
    }

    // Prevent Enter to avoid form submission
    // when directive is used inside a form
    if (e.keyCode !== 13) {
      return;
    }

    e.preventDefault();

  }

  /**
   * @param {Object} e
   */
  function onFocusChange(e) {

    vm.isFocused = e.type === 'focus' ? true : false;
    vm.isFocused && vm.onFocus && vm.onFocus();

  }

  /**
   * @ngdoc method
   * @name change
   * @methodOf adjust.controller:SearchController
   *
   * @description
   * Input change callback which triggers the parent callback if exists
   *
   */
  function change() {

    if (!angular.isFunction(vm.onChange) || vm.disabled) { return; }

    _setQuery(vm.searchTerm);

    $timeout(function() {
      vm.onChange({term: vm.searchTerm});
    });

  }

  /**
   * @ngdoc method
   * @name reset
   * @methodOf adjust.controller:SearchController
   *
   * @description
   * Reset search to empty string and trigger the change callback
   *
   */
  function reset() {

    vm.changeDelayed.cancel();

    vm.searchTerm = '';

    change();
  }

  /**
   * @ngdoc method
   * @name _setQuery
   * @methodOf adjust.controller:SearchController
   *
   * @description
   * Set the query string in the url
   *
   * @param {string|null} value query string
   * @private
   */
  function _setQuery(value) {

    value = value || null;

    if (vm.query) {
      $location.search(vm.query, value).replace();
    }

  }

  /**
   * @ngdoc method
   * @name _handleReset
   * @methodOf adjust.controller:SearchController
   *
   * @description
   * Reset without callback invoke
   *
   * @private
   */
  function _handleReset() {

    vm.changeDelayed.cancel();

    vm.searchTerm = '';

    _setQuery(null);

  }

  /**
   * @ngdoc method
   * @name _handleDestroy
   * @methodOf adjust.controller:SearchController
   *
   * @description
   * Ensure to remove query string from url when directive is destroyed
   *
   * @private
   */
  function _handleDestroy() {

    _setQuery(null);

  }
}


export { SearchController, search };
